/**
 * **The midway window** (ON21, `rd05`): what an enrolled door device at the midway booth opens to
 * at /booth. One box takes a scanned or typed code and uses it; the same box finds an order by its
 * code, a name, or a phone (seven digits or more) when the scan fails, and each bracelet on it is
 * used with one tap. A second use says when and where the first was. An order paid after presale
 * closed is honored here and flagged. The counters at the side are the redemption engine's.
 */
import { useEffect, useState, type ReactNode } from 'react';
import { api, sentence } from '../../shell/api.js';

interface Unit {
  code: string;
  what: string;
  forName: string | null;
  redeemed: string | null;
}
interface Found {
  orderId: string;
  code: string;
  buyer: string;
  afterClose: boolean;
  units: Unit[];
  says: string;
}
interface Counters {
  redeemedToday: number;
  lastAt: string | null;
  sold: number;
  unredeemed: number;
  says: string;
}

const UNIT = /^[A-Z0-9]{6}-[A-Z0-9]{3}$/;

export function MidwayStation({
  label,
  onClose,
}: {
  label: string;
  onClose: () => void;
}): ReactNode {
  const [q, setQ] = useState('');
  const [found, setFound] = useState<Found[] | null>(null);
  const [said, setSaid] = useState<string | null>(null);
  const [refusal, setRefusal] = useState<string | null>(null);
  const [counters, setCounters] = useState<Counters | null>(null);
  const [busy, setBusy] = useState(false);

  const count = (): void => {
    api<Counters>('GET', '/commerce/window')
      .then(setCounters)
      .catch(() => undefined);
  };
  useEffect(count, []);

  const use = (code: string, how: 'scanned' | 'typed' | 'name' | 'phone'): void => {
    setBusy(true);
    setRefusal(null);
    api<{ says: string; order: Found }>('POST', '/commerce/window/redeem', { code, how })
      .then((r) => {
        setSaid(r.says);
        setFound((f) =>
          f ? f.map((o) => (o.orderId === r.order.orderId ? r.order : o)) : [r.order],
        );
        setQ('');
        count();
      })
      .catch((e: unknown) => {
        setRefusal(sentence(e));
      })
      .finally(() => {
        setBusy(false);
      });
  };

  const go = (): void => {
    const typed = q.trim().toUpperCase();
    setSaid(null);
    setRefusal(null);
    if (UNIT.test(typed)) {
      use(typed, 'scanned');
      return;
    }
    setBusy(true);
    api<{ rows: Found[]; says: string }>(
      'GET',
      `/commerce/window/find?q=${encodeURIComponent(q.trim())}`,
    )
      .then((r) => {
        setFound(r.rows);
        if (r.rows.length === 0) setRefusal(r.says);
      })
      .catch((e: unknown) => {
        setRefusal(sentence(e));
      })
      .finally(() => {
        setBusy(false);
      });
  };
  const how = (): 'name' | 'phone' | 'typed' =>
    /\d{7,}/.test(q.replace(/\D/g, ''))
      ? 'phone'
      : /^[A-Za-z]/.test(q) && !/^[A-Z0-9]{6}$/.test(q.toUpperCase())
        ? 'name'
        : 'typed';

  return (
    <div className="booth" data-screen="booth" data-booth="midway">
      <header className="booth__bar">
        <span className="booth__brand">CIDER · MIDWAY</span>
        <span className="booth__station">THE WINDOW · {label.toUpperCase()}</span>
        <span className="booth__grow" />
        <button type="button" className="booth__close" onClick={onClose}>
          Close the window
        </button>
      </header>
      <div className="booth__body">
        <main className="booth__main">
          {/* RG-03 · 04 · 05 (Rebuild 36): ready with the count biggest; redeemed in green; refused
              in gold with the rule's sentence and the count unchanged. */}
          {refusal ? (
            <section className="rg rg--refused" data-testid="rg-state">
              <p className="rg__word">NOT VALID</p>
              <p className="rg__says" role="alert">
                {refusal}
              </p>
              <p className="rg__left">
                Count unchanged <b className="rg__n">{counters ? counters.unredeemed : '—'}</b>
              </p>
            </section>
          ) : said ? (
            <section className="rg rg--ok" data-testid="rg-state">
              <p className="rg__word">REDEEMED</p>
              <p className="rg__says" role="status">
                {said}
              </p>
              {/* The drawing names the band's color; nothing records it yet (James, Oct 16). */}
              <p className="rg__left" data-testid="rg-band">
                Hand them a band · color: <b>NO DATA</b>
              </p>
              <p className="rg__left">
                <b className="rg__n">{counters ? counters.unredeemed : '—'}</b> left
              </p>
            </section>
          ) : (
            <section className="rg rg--ready" data-testid="rg-state">
              <h1 className="rg__word">SCAN THE CODE</h1>
              <p className="rg__says">Pull the trigger. Point at their phone or paper.</p>
              <p className="rg__left">
                <b className="rg__n">{counters ? counters.unredeemed : '—'}</b> left
              </p>
              <p className="booth__fine">
                source: the booth’s count{counters ? ` · ${String(counters.sold)} sold` : ''}
              </p>
            </section>
          )}
          <p className="booth__eyebrow">NO CODE?</p>
          <form
            className="booth__find"
            onSubmit={(e) => {
              e.preventDefault();
              go();
            }}
          >
            <input
              className="booth__search"
              aria-label="Code, name or phone"
              placeholder="Scan the code — or type the order, a name, a phone. Every band has an order behind it."
              value={q}
              autoFocus
              onChange={(e) => {
                setQ(e.target.value);
              }}
            />
            <button type="submit" className="booth__btn" disabled={busy || q.trim().length < 2}>
              Go
            </button>
          </form>
          {(found ?? []).map((o) => (
            <section key={o.orderId} className="booth__card" data-testid="midway-order">
              <div className="booth__cardhead">
                {o.code} · {o.buyer}
                {o.afterClose ? (
                  <span className="booth__chip booth__chip--short">PAID LATE · HONORED</span>
                ) : null}
              </div>
              <p className="booth__fine">{o.says}</p>
              {o.units.map((u) => (
                <div key={u.code} className="booth__row">
                  <span className="booth__k">
                    {u.what}
                    {u.forName ? ` · ${u.forName}` : ''} · {u.code}
                  </span>
                  {u.redeemed ? (
                    <span className="booth__chip booth__chip--in">
                      USED {u.redeemed.toUpperCase()}
                    </span>
                  ) : (
                    <button
                      type="button"
                      className="booth__btn"
                      disabled={busy}
                      onClick={() => {
                        use(u.code, how());
                      }}
                    >
                      Use this one
                    </button>
                  )}
                </div>
              ))}
            </section>
          ))}
        </main>
        <aside className="booth__side">
          <p className="booth__sidehead">TODAY AT THIS WINDOW</p>
          {counters ? (
            <>
              <p className="booth__count" data-testid="midway-today">
                {counters.redeemedToday} redeemed
              </p>
              <p className="booth__fine">
                {counters.lastAt ? `Last at ${counters.lastAt}` : 'None yet today'}
              </p>
              <p className="booth__fine">
                {counters.unredeemed} of {counters.sold} not yet used
              </p>
            </>
          ) : null}
        </aside>
      </div>
    </div>
  );
}
